<template>
  <section>
    <v-container>
      <v-row>
        <v-col class="col-8 px-6 pb-0">
          <booking-date-picker
            :range="false"
            @selected="dateRangeChanged"
            @today="goToToday"
          />
        </v-col>

        <v-col class="col-4 pb-0">
          <v-row>
            <v-col class="col-12 d-flex justify-end pt-0">
              <enter-booking-btn class="mr-4" />
            </v-col>
            <v-col class="col-12 d-flex justify-end">
              <v-btn
                class="mr-4"
                :disabled="bookings.length === 0"
                color="primary"
                outlined
                large
                @click="downloadExcel"
              >
                <i class="fad fa-file-spreadsheet mr-2" />
                {{ $t('bookingComplete.excelDownload') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="col-8 pt-0">
          <v-data-table
            :headers="headers"
            :items="bookings"
            :items-per-page="-1"
            :no-data-text="$t('dataTable.noDataAvailable')"
            :no-results-text="$t('dataTable.noResultFound')"
            :footer-props="{
              itemsPerPageText: $t('general.rowsPerPage'),
              itemsPerPageAllText: $t('dataFooter.itemsPerPageAllText'),
            }"
            :loading="isLoading"
            :loading-text="$t('general.loadingData')"
            item-key="pe_productId"
            :locale="$store.getters.getLanguageLocale().substr(0, 2)"
          >
            <template #item="props">
              <tr
                :set="
                  (productInstance = getProductFromAggregatedBooking(
                    props.item
                  ))
                "
              >
                <td>
                  {{ $t(props.item.translation_product) }}
                </td>
                <td class="text-center">
                  {{ props.item.totalPeopleCount }}
                </td>

                <!-- capacities -->
                <td
                  :set="
                    (capLength = productInstance
                      .getProductCapacity()
                      .getCapacityValues().length)
                  "
                >
                  <div v-if="capLength === 0">
                    <span class="fal fa-check green--text mr-2" />
                    {{ $t('bookingAggregated.noCapacityLimit') }}
                  </div>
                  <booking-progress-bar
                    v-else-if="capLength === 1"
                    :product="productInstance"
                    :capacity="
                      productInstance
                        .getProductCapacity()
                        .getCapacityValues()[0]
                    "
                    :start-date="dateRange.from | formatDate"
                    :end-date="dateRange.to | formatDate"
                  />
                  <div
                    v-for="cap in productInstance
                      .getProductCapacity()
                      .getFilteredCapacityValues(true, false)"
                    v-else
                    :key="cap.getTranslation()"
                    class="my-1"
                  >
                    <booking-progress-bar
                      :product="productInstance"
                      :capacity="cap"
                      :start-date="dateRange.from | formatDate"
                      :end-date="dateRange.to | formatDate"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import BookingCalendar from '../capacityCalendar/CapacityCalendar'
import EnterBookingBtn from './EnterBookingBtn'
import BookingProgressBar from '../products/CapacityProgressBar'
import BookingDatePicker from './BookingDatePicker'
import XLSX from 'xlsx'
import XlsxHelper from '../../classes-shared/XLSX/XlsxHelper'
import BookingAggregatedService from '../../classes-shared/bookings/BookingAggregatedService'
import Product from '../../classes-shared/products/Product'

export default {
  name: 'BookingAggregated',

  components: {
    BookingDatePicker,
    BookingProgressBar,
    EnterBookingBtn,
  },

  data() {
    return {
      bookingsService: new BookingAggregatedService(),
      headers: [
        {
          text: this.$t('shopBackendBookingComplete.product'),
          width: '30%',
        },
        {
          text: this.$t('shopBackendBookingComplete.peopleCount'),
          width: '20%',
          align: 'center',
        },
        {
          text: this.$t('shopBackendBookingComplete.capacity'),
          width: '60%',
        },
      ],
      dateRange: null,
      bookings: [],
      isLoading: false,
      dateFormat: 'YYYY-MM-DDTHH:mm:ss.SS',
    }
  },

  methods: {
    dateRangeChanged(newDateRange) {
      // also initializes the data fetching on mounted
      this.dateRange = newDateRange
      this.onFilterOrDateChange()
    },
    async loadAggregatedBookings() {
      const date = this.$options.filters.formatDate(
        this.dateRange.from,
        this.dateFormat
      )
      this.bookings = await this.bookingsService.fetchAggregatedBookings(
        date,
        date
      )
    },
    async onFilterOrDateChange() {
      this.isLoading = true
      await this.loadAggregatedBookings()
      this.isLoading = false
    },
    getProductFromAggregatedBooking(aggregatedModel) {
      let tmpProduct = new Product({
        id: aggregatedModel.pe_productId,
        translation: aggregatedModel.translation_product,
      })
      tmpProduct.setProductCapacity(aggregatedModel.capacity)

      return tmpProduct
    },
    async downloadExcel() {
      EventBus.$emit('spinnerShow')
      let workBook = XLSX.utils.book_new()
      await XLSX.utils.book_append_sheet(
        workBook,
        await this.getWorkSheet(),
        this.$t('shopBackendBookingComplete.aggregatedBookings')
      )
      EventBus.$emit('spinnerHide')
      XLSX.writeFile(
        workBook,
        this.$t('shopBackendBookingComplete.aggregatedBookings') + '.xlsx'
      )
    },
    async getWorkSheet() {
      let jsonWorksheet = []
      this.bookings.forEach((bookingEntry) => {
        jsonWorksheet.push({
          product: this.$t(bookingEntry.translation_product),
          peopleCount: bookingEntry.totalPeopleCount,
        })
      })

      let workSheet = XLSX.utils.json_to_sheet(jsonWorksheet)
      let xlsxHelper = new XlsxHelper(workSheet)
      await xlsxHelper.setHeader([
        this.$t('shopBackendBookingComplete.product'),
        this.$t('shopBackendBookingComplete.peopleCount'),
      ])

      // set auto width
      await xlsxHelper.setColumnWidthAuto()
      return xlsxHelper.getWorkSheet()
    },
    goToToday() {},
  },
}
</script>

<style scoped lang="scss"></style>
